import React, { useCallback, useEffect, useState } from 'react';
import { X, ThumbsUp, ThumbsDown, SkipForward, Sparkle, CheckCircle } from '@phosphor-icons/react';

/*
  WeeklyRatingPass
  ----------------
  The "Sunday evening ritual" — batch-rate every dish cooked this week with
  one tap each. 👍 = love / 👎 = never suggest / ⏭ = skip.

  Auto-shows a banner on the Meals page when:
    - the household cooked ≥1 dish this week AND
    - the pass hasn't been submitted or dismissed for this week

  Props:
    api            (axios)
    onDone?        (fn)  — fired after successful submit or dismiss

  Behaviour:
    • Fetches /api/meals/weekly-rating-pass on mount
    • If due → renders a compact banner with a "Rate now" button
    • Clicking Rate opens the modal — one card per dish with 3 big buttons
    • Submit posts to /submit; Not-now posts to /dismiss (silent)
*/

const SLOT_ICONS = { breakfast: '🌅', lunch: '☀️', dinner: '🌙', snacks: '🍪' };
const SLOT_LABELS = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snacks: 'Hi-tea' };

export default function WeeklyRatingPass({ api, onDone }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [ratings, setRatings] = useState({}); // { dishKey: 'up'|'down'|'skip' }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {counts}

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/meals/weekly-rating-pass');
      setStatus(r.data || null);
    } catch (_) { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Auto-open if URL says so (?rating_pass=1)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('rating_pass') === '1' && status?.due) setOpen(true);
  }, [status]);

  const setRating = (key, val) => setRatings(prev => ({ ...prev, [key]: val }));

  const openModal = () => {
    // Pre-fill everything with 'skip' so users can just tap up/down where relevant
    const init = {};
    (status?.dishes || []).forEach(d => { init[d.dish_key] = 'skip'; });
    setRatings(init);
    setSubmitted(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!status?.dishes?.length) return;
    setSubmitting(true);
    try {
      const items = status.dishes.map(d => ({
        dish_name: d.dish_name,
        dish_key: d.dish_key,
        slot: d.slot,
        date: d.date,
        plan_id: d.plan_id,
        rating: ratings[d.dish_key] || 'skip',
      }));
      const r = await api.post('/meals/weekly-rating-pass/submit', { items });
      setSubmitted(r.data?.counts || { up: 0, down: 0, skip: items.length });
      // Refresh status so banner disappears
      await fetchStatus();
      onDone && onDone();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to submit ratings.');
    }
    setSubmitting(false);
  };

  const dismiss = async () => {
    try {
      await api.post('/meals/weekly-rating-pass/dismiss');
      await fetchStatus();
      setOpen(false);
    } catch (_) { setOpen(false); }
  };

  // Nothing to show
  if (!status) return null;
  if (!status.due && !open) return null;

  const dishes = status.dishes || [];
  const upCount = Object.values(ratings).filter(v => v === 'up').length;
  const downCount = Object.values(ratings).filter(v => v === 'down').length;
  const skipCount = dishes.length - upCount - downCount;

  return (
    <>
      {/* --- BANNER (auto-shown on planner when due) --- */}
      {status.due && !open && (
        <div
          data-testid="weekly-rating-pass-banner"
          className="rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap"
          style={{
            background: 'linear-gradient(135deg, rgba(255,107,53,0.12), rgba(168,85,247,0.08))',
            border: '1px solid rgba(255,107,53,0.30)',
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(255,107,53,0.20)' }}>
              <Sparkle size={20} weight="fill" className="text-[#FF6B35]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white">
                Sunday ritual — how was this week&apos;s food?
              </p>
              <p className="text-[11px] text-neutral-300 truncate">
                {dishes.length} dish{dishes.length !== 1 ? 'es' : ''} cooked this week. Takes ~30 seconds. Teaches the AI what your family loves.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={dismiss}
              data-testid="weekly-rating-pass-dismiss"
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-neutral-400 hover:text-white transition-colors"
            >
              Not now
            </button>
            <button
              onClick={openModal}
              data-testid="weekly-rating-pass-open"
              className="px-4 py-1.5 rounded-lg text-[11px] font-bold bg-[#FF6B35] text-white hover:bg-[#ff7f50] transition-colors inline-flex items-center gap-1.5"
            >
              <Sparkle size={11} weight="fill" /> Rate now
            </button>
          </div>
        </div>
      )}

      {/* --- MODAL --- */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          data-testid="weekly-rating-pass-modal"
          onClick={() => !submitting && setOpen(false)}
        >
          <div
            className="w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl overflow-hidden"
            style={{ background: '#161616', border: '1px solid rgba(255,255,255,0.08)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <div className="min-w-0">
                <p className="text-base font-bold text-white">This week&apos;s cooked meals</p>
                <p className="text-[11px] text-neutral-400">
                  {dishes.length} dish{dishes.length !== 1 ? 'es' : ''} · tap 👍 or 👎 for the ones that matter
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="weekly-rating-pass-close"
                className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {/* Post-submit success view */}
            {submitted && (
              <div className="p-8 text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{ background: 'rgba(16,185,129,0.15)' }}>
                  <CheckCircle size={28} weight="fill" className="text-emerald-400" />
                </div>
                <p className="text-lg font-bold text-white">Done for this week! 🎉</p>
                <p className="text-sm text-neutral-300 mt-1">
                  {submitted.up} loved · {submitted.down} won&apos;t-repeat · {submitted.skip} skipped
                </p>
                <p className="text-[11px] text-neutral-500 mt-4 max-w-md mx-auto">
                  The AI Chef will honor these preferences from your next menu onwards.
                  See the &quot;What the AI has learned about you&quot; card on your Food Profile.
                </p>
                <button
                  onClick={() => setOpen(false)}
                  className="mt-5 px-5 py-2 rounded-xl text-sm font-bold bg-[#FF6B35] text-white hover:bg-[#ff7f50]"
                  data-testid="weekly-rating-pass-close-success"
                >
                  Close
                </button>
              </div>
            )}

            {/* Rating list */}
            {!submitted && (
              <>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {dishes.length === 0 ? (
                    <p className="text-center text-neutral-500 py-12 text-sm italic">
                      No cooked meals to rate this week.
                    </p>
                  ) : dishes.map((d, i) => {
                    const val = ratings[d.dish_key] || 'skip';
                    const slotIcon = SLOT_ICONS[d.slot] || '🍽️';
                    const slotLabel = SLOT_LABELS[d.slot] || d.slot || '';
                    const dateShort = d.date ? new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit' }) : '';
                    return (
                      <div
                        key={d.dish_key || `${d.dish_name}-${i}`}
                        data-testid={`weekly-rating-row-${i}`}
                        className={`flex items-center justify-between gap-2 rounded-xl p-3 transition-colors ${val === 'up' ? 'ring-1 ring-emerald-500/40' : val === 'down' ? 'ring-1 ring-red-500/40' : ''}`}
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-white truncate">{d.dish_name}</p>
                          <p className="text-[10px] text-neutral-400 flex items-center gap-2">
                            <span>{slotIcon} {slotLabel}</span>
                            {dateShort && <span>· {dateShort}</span>}
                            {d.cook_name && <span>· by {d.cook_name}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => setRating(d.dish_key, 'up')}
                            data-testid={`weekly-rating-up-${i}`}
                            title="Loved it — make more"
                            className={`p-2 rounded-xl transition-all ${val === 'up'
                              ? 'bg-emerald-500/30 text-emerald-200 scale-110'
                              : 'bg-white/5 text-neutral-400 hover:bg-emerald-500/15 hover:text-emerald-300'}`}
                          >
                            <ThumbsUp size={16} weight={val === 'up' ? 'fill' : 'regular'} />
                          </button>
                          <button
                            onClick={() => setRating(d.dish_key, 'down')}
                            data-testid={`weekly-rating-down-${i}`}
                            title="Never suggest again"
                            className={`p-2 rounded-xl transition-all ${val === 'down'
                              ? 'bg-red-500/30 text-red-200 scale-110'
                              : 'bg-white/5 text-neutral-400 hover:bg-red-500/15 hover:text-red-300'}`}
                          >
                            <ThumbsDown size={16} weight={val === 'down' ? 'fill' : 'regular'} />
                          </button>
                          <button
                            onClick={() => setRating(d.dish_key, 'skip')}
                            data-testid={`weekly-rating-skip-${i}`}
                            title="Skip — no opinion"
                            className={`p-2 rounded-xl transition-all ${val === 'skip'
                              ? 'bg-neutral-700/50 text-neutral-200'
                              : 'bg-white/5 text-neutral-500 hover:bg-white/10 hover:text-neutral-300'}`}
                          >
                            <SkipForward size={16} weight={val === 'skip' ? 'fill' : 'regular'} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="p-3 border-t border-white/5 flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[10px] text-neutral-400 flex items-center gap-3" data-testid="weekly-rating-summary">
                    <span className="text-emerald-300 font-semibold">👍 {upCount} love</span>
                    <span className="text-red-300 font-semibold">👎 {downCount} never</span>
                    <span>⏭ {skipCount} skip</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={dismiss}
                      disabled={submitting}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-neutral-400 hover:text-white"
                      data-testid="weekly-rating-pass-dismiss-modal"
                    >
                      Not now
                    </button>
                    <button
                      onClick={submit}
                      disabled={submitting || dishes.length === 0}
                      data-testid="weekly-rating-pass-submit"
                      className="px-4 py-1.5 rounded-lg text-[11px] font-bold bg-[#FF6B35] text-white hover:bg-[#ff7f50] disabled:opacity-40 inline-flex items-center gap-1.5"
                    >
                      <Sparkle size={11} weight="fill" />
                      {submitting ? 'Saving…' : `Save ratings (${upCount + downCount})`}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
